import React, { useEffect, useRef, useState } from 'react';
import FormError from '../../../FormError';
import { apiFetch, errorText } from '../shared/api';
import LogoPicker from '../add/LogoPicker';
import CollapsibleCardHeader from './CollapsibleCardHeader';
import { formatPhoneAsTyped } from '../add/formatPhoneAsTyped';
import { hostFromUrl } from '../add/DemoAccountAdd';
import { formUrlEncoded } from './format';
import Spinner from '../shared/Spinner';

const SLUG_CHECK_DEBOUNCE_MS = 300;
const EMPTY_LOGO = { candidateUrl: null, file: null };
const EMPTY_LISTING_OVERRIDE = { seq: 0, fields: {} };
const LISTING_OVERRIDE_FIELDS = ['name', 'address', 'phone', 'url'];

function trimmed(value) {
    return (value ?? '').toString().trim();
}

const RENDERED_ERROR_FIELDS = [
    'name', 'slug', 'address', 'phone', 'url', 'display_url_as', 'industry_id', 'custom_description_placeholder',
];

// The query string only carries a cache-buster, so it must not count as a different logo.
function logoIdentity(logoUrl) {
    return (logoUrl ?? '').split('?')[0];
}

function fieldError(errors, field) {
    return errors && errors[field] && errors[field][0];
}

function buildPatch({
    demo, name, slug, address, showAddress, phone, url, displayUrlAs,
    industryId, customDescriptionPlaceholder, logo, logoTouched,
}) {
    const patch = {};
    const originalIndustryId = demo.industry ? String(demo.industry.id) : '';

    if (trimmed(name) !== trimmed(demo.name)) {
        patch.name = name;
    }
    if (trimmed(slug) !== trimmed(demo.slug)) {
        patch.slug = slug;
    }
    if (trimmed(address) !== trimmed(demo.address)) {
        patch.address = address;
    }
    if (showAddress !== Boolean(demo.show_address)) {
        patch.show_address = showAddress;
    }
    if (trimmed(phone) !== trimmed(demo.phone)) {
        patch.phone = phone;
    }
    if (trimmed(url) !== trimmed(demo.url)) {
        patch.url = url;
    }
    if (trimmed(displayUrlAs) !== trimmed(demo.display_url_as)) {
        patch.display_url_as = displayUrlAs;
    }
    if (industryId !== originalIndustryId) {
        patch.industry_id = industryId;
    }
    if (trimmed(customDescriptionPlaceholder) !== trimmed(demo.custom_description_placeholder || '')) {
        patch.custom_description_placeholder = customDescriptionPlaceholder;
    }
    // A pending upload carries the logo in the multipart body; a URL beside it could be stale.
    if (!logo.file && logoTouched && logo.candidateUrl && logoIdentity(logo.candidateUrl) !== logoIdentity(demo.logo_url)) {
        patch.logo_url = logo.candidateUrl;
    }

    return patch;
}

export default function EditDemoAccount({
    demo,
    endpoints,
    industries,
    brandedLinkBase,
    googleImageSearchBase,
    limits,
    listingOverride = EMPTY_LISTING_OVERRIDE,
    onDemoChange,
    onIndustryChange,
    onToast,
}) {
    const [name, setName] = useState(demo.name || '');
    const [slug, setSlug] = useState(demo.slug || '');
    const [address, setAddress] = useState(demo.address || '');
    const [showAddress, setShowAddress] = useState(Boolean(demo.show_address));
    const [phone, setPhone] = useState(demo.phone || '');
    const [url, setUrl] = useState(demo.url || '');
    const [displayUrlAs, setDisplayUrlAs] = useState(demo.display_url_as || '');
    const [industryId, setIndustryId] = useState(demo.industry ? String(demo.industry.id) : '');
    const [customDescriptionPlaceholder, setCustomDescriptionPlaceholder] = useState(demo.custom_description_placeholder || '');
    const [logo, setLogo] = useState(EMPTY_LOGO);
    const [logoTouched, setLogoTouched] = useState(false);

    const [slugStatus, setSlugStatus] = useState(null);
    const [slugSuggestion, setSlugSuggestion] = useState(null);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [slugChecking, setSlugChecking] = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const mountedRef = useRef(true);
    // LogoPicker auto-resolves a candidate for the current domain right after mount (and again
    // whenever `domain` changes) and reports it through the same onChange as a real user pick.
    // This flag lets handleLogoChange tell that first, automatic call apart from one the user
    // actually caused (upload, or any later fileless call once the auto call for this domain
    // already landed), so an untouched Save never overwrites the real logo.
    const expectAutoLogoRef = useRef(true);
    const lastLogoUrlRef = useRef(logoIdentity(demo.logo_url));
    // The picker below is keyed on the logo url, so a replaced logo remounts it and it reports its
    // current logo again. Re-arming during render, not in an effect, because the remounted child's
    // effect runs before this component's own effects.
    if (lastLogoUrlRef.current !== logoIdentity(demo.logo_url)) {
        lastLogoUrlRef.current = logoIdentity(demo.logo_url);
        expectAutoLogoRef.current = true;
    }

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    useEffect(() => {
        const fields = listingOverride.fields || {};
        LISTING_OVERRIDE_FIELDS.forEach((field) => {
            if (!Object.prototype.hasOwnProperty.call(fields, field)) {
                return;
            }
            const value = fields[field] || '';
            if (field === 'name') setName(value);
            if (field === 'address') setAddress(value);
            if (field === 'phone') setPhone(value);
            if (field === 'url') setUrl(value);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingOverride.seq]);

    useEffect(() => {
        if (trimmed(slug) === trimmed(demo.slug)) {
            setSlugStatus(null);
            setSlugSuggestion(null);
            setSlugChecking(false);
            return undefined;
        }

        let superseded = false;
        const timer = setTimeout(() => {
            setSlugChecking(true);
            apiFetch(endpoints.slugCheck, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: formUrlEncoded([['slug', slug], ['tellmyco_id', demo.id]]),
            }).then(({ status, json }) => {
                if (!mountedRef.current || status < 200 || status >= 300) {
                    return;
                }
                const available = json?.available === true;
                setSlugStatus(available ? 'available' : 'taken');
                setSlugSuggestion(available ? null : (json?.suggestion || null));
            }).catch((error) => {
                console.error(error);
            }).finally(() => {
                if (mountedRef.current && !superseded) {
                    setSlugChecking(false);
                }
            });
        }, SLUG_CHECK_DEBOUNCE_MS);

        return () => {
            superseded = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, demo.slug, demo.id, endpoints.slugCheck]);

    const handleIndustryChange = (event) => {
        const selectedId = event.target.value;
        setIndustryId(selectedId);
        const industry = industries.find((candidate) => String(candidate.id) === selectedId);
        if (industry) {
            onIndustryChange(industry);
        }
    };

    const patch = buildPatch({
        demo, name, slug, address, showAddress, phone, url, displayUrlAs,
        industryId, customDescriptionPlaceholder, logo, logoTouched,
    });
    const isDirty = Object.keys(patch).length > 0 || Boolean(logo.file);

    const domain = hostFromUrl(trimmed(url) || trimmed(demo.url));
    const slugRemaining = limits.slug - slug.length;
    const slugRemainingBadgeClass = slugRemaining < 0 ? 'badge-danger' : (slugRemaining <= 5 ? 'badge-warning' : 'badge-light');

    useEffect(() => {
        expectAutoLogoRef.current = true;
        // A domain change invalidates any earlier touch: the candidate LogoPicker is
        // about to auto-resolve for the new domain has not been confirmed by the user yet.
        setLogoTouched(false);
    }, [domain]);

    const handleLogoChange = (next) => {
        if (next.file || !expectAutoLogoRef.current) {
            setLogoTouched(true);
        } else {
            expectAutoLogoRef.current = false;
            setLogoTouched(false);
        }
        setLogo(next);
    };

    const handleSave = async () => {
        setSubmitting(true);
        setErrors({});

        let method = 'PATCH';
        let body;
        const headers = {};

        if (logo.file) {
            method = 'POST';
            const formData = new FormData();
            formData.append('_method', 'PATCH');
            Object.entries(patch).forEach(([key, value]) => {
                if (typeof value === 'boolean') {
                    formData.append(key, value ? '1' : '0');
                } else if (value !== null && value !== undefined) {
                    formData.append(key, value);
                }
            });
            formData.append('logo', logo.file);
            body = formData;
        } else {
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify(patch);
        }

        try {
            const { status, json } = await apiFetch(endpoints.update, { method, body, headers });

            if (!mountedRef.current) {
                return;
            }

            if (status >= 200 && status < 300) {
                // Cleared before onDemoChange: the remounted picker seeds its file from `logo`.
                setLogo(EMPTY_LOGO);
                setLogoTouched(false);
                onDemoChange(json.data);
                onToast({ type: 'success', text: __('Demo account saved') });
            } else if (status === 422) {
                const validationErrors = json?.errors || {};
                setErrors(validationErrors);
                const unrenderedMessages = Object.entries(validationErrors)
                    .filter(([field]) => !RENDERED_ERROR_FIELDS.includes(field))
                    .flatMap(([, messages]) => messages);
                if (Object.keys(validationErrors).length === 0) {
                    console.error('Demo account update rejected with no field errors', json);
                    onToast({ type: 'error', text: errorText(json, __('Something went wrong. Please try again.')) });
                } else if (unrenderedMessages.length > 0) {
                    console.error('Demo account update rejected', validationErrors);
                    onToast({ type: 'error', text: unrenderedMessages.join(' ') });
                }
            } else {
                onToast({ type: 'error', text: errorText(json, __('Something went wrong. Please try again.')) });
            }
        } catch (error) {
            console.error(error);
            if (mountedRef.current) {
                onToast({ type: 'error', text: __('Something went wrong. Please try again.') });
            }
        } finally {
            if (mountedRef.current) {
                setSubmitting(false);
            }
        }
    };

    return (
        <div className="card demo-edit-form" data-testid="demo-edit-form">
            <CollapsibleCardHeader
                title={__('Edit Demo Account')}
                collapsed={collapsed}
                onToggle={() => setCollapsed((prev) => !prev)}
                controlsId="demo-edit-form-body"
            />

            <div className={`card-body${collapsed ? ' d-none' : ''}`} id="demo-edit-form-body">
                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="demo-edit-slug">{__('Branded Link')}</label>
                            <div className="input-group">
                                <div className="input-group-prepend">
                                    <span className="input-group-text">{brandedLinkBase}</span>
                                </div>
                                <input
                                    id="demo-edit-slug"
                                    type="text"
                                    className="form-control"
                                    maxLength={limits.slug}
                                    value={slug}
                                    onChange={(event) => setSlug(event.target.value)}
                                />
                            </div>
                            <div className="demo-island-slug-meta">
                                <small className="form-text text-muted">
                                    {__('Branded Link is :base:placeholder', { ':base': brandedLinkBase, ':placeholder': 'XYZ' })}
                                </small>
                                <span className={`badge demo-island-slug-badge demo-island-slug-remaining ${slugRemainingBadgeClass}`}>
                                    {__('Characters Remaining: :n', { ':n': slugRemaining })}
                                </span>
                                {slugChecking && <Spinner label={__('Checking availability')} />}
                                {slugStatus &&
                                    <span
                                        data-testid="demo-edit-slug-badge"
                                        className={`badge demo-island-slug-badge ${slugStatus === 'available' ? 'badge-light' : 'badge-danger'}`}
                                    >
                                        {slugStatus === 'available' ? __('Available') : __('Taken')}
                                    </span>
                                }
                            </div>
                            {slugStatus === 'taken' && slugSuggestion &&
                                <small className="form-text text-danger">
                                    {__('This branded link is already taken. Try :suggestion.', { ':suggestion': slugSuggestion })}
                                </small>
                            }
                            <FormError error_message={fieldError(errors, 'slug')} />
                        </div>

                        <div className="form-group">
                            <label htmlFor="demo-edit-address">{__('Address')}</label>
                            <input
                                id="demo-edit-address"
                                type="text"
                                className="form-control"
                                value={address}
                                onChange={(event) => setAddress(event.target.value)}
                            />
                            <FormError error_message={fieldError(errors, 'address')} />
                        </div>

                        <div className="custom-control custom-checkbox mb-3">
                            <input
                                id="demo-edit-show-address"
                                type="checkbox"
                                className="custom-control-input"
                                checked={showAddress}
                                onChange={(event) => setShowAddress(event.target.checked)}
                            />
                            <label className="custom-control-label" htmlFor="demo-edit-show-address">
                                {__('Show address on Feedback page')}
                            </label>
                        </div>

                        <div className="form-group">
                            <label htmlFor="demo-edit-phone">{__('Phone')}</label>
                            <input
                                id="demo-edit-phone"
                                type="tel"
                                className="form-control"
                                value={phone}
                                onChange={(event) => setPhone(formatPhoneAsTyped(event.target.value, phone))}
                            />
                            <FormError error_message={fieldError(errors, 'phone')} />
                        </div>

                        <div className="form-group">
                            <label htmlFor="demo-edit-display-url-as">{__('Display URL as')}</label>
                            <input
                                id="demo-edit-display-url-as"
                                type="text"
                                className="form-control"
                                value={displayUrlAs}
                                onChange={(event) => setDisplayUrlAs(event.target.value)}
                            />
                            <FormError error_message={fieldError(errors, 'display_url_as')} />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="demo-edit-name">{__('Name')}</label>
                            <input
                                id="demo-edit-name"
                                type="text"
                                className="form-control"
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                            />
                            <FormError error_message={fieldError(errors, 'name')} />
                        </div>

                        <div className="form-group">
                            <label htmlFor="demo-edit-url">{__('URL')}</label>
                            <input
                                id="demo-edit-url"
                                type="text"
                                className="form-control"
                                value={url}
                                onChange={(event) => setUrl(event.target.value)}
                            />
                            <FormError error_message={fieldError(errors, 'url')} />
                        </div>

                        <div className="form-group">
                            <label htmlFor="demo-edit-industry">{__('Industry')}</label>
                            <select
                                id="demo-edit-industry"
                                className="form-control"
                                value={industryId}
                                onChange={handleIndustryChange}
                            >
                                <option value="">{__('Select an industry')}</option>
                                {industries.map((industry) => (
                                    <option key={industry.id} value={industry.id}>{industry.name}</option>
                                ))}
                            </select>
                            <FormError error_message={fieldError(errors, 'industry_id')} />
                        </div>

                        <div className="form-group">
                            <label htmlFor="demo-edit-description-placeholder">{__('Custom description placeholder')}</label>
                            <textarea
                                id="demo-edit-description-placeholder"
                                className="form-control"
                                value={customDescriptionPlaceholder}
                                onChange={(event) => setCustomDescriptionPlaceholder(event.target.value)}
                            />
                            <FormError error_message={fieldError(errors, 'custom_description_placeholder')} />
                        </div>
                    </div>
                </div>

                <LogoPicker
                    key={logoIdentity(demo.logo_url)}
                    endpoints={endpoints}
                    domain={domain}
                    businessName={name}
                    googleImageSearchBase={googleImageSearchBase}
                    currentUrl={demo.logo_url || null}
                    value={logo}
                    onChange={handleLogoChange}
                />

                <div className="demo-island-save-wrapper demo-island-save-left">
                    <button
                        type="button"
                        className="btn btn-primary"
                        data-testid="demo-edit-save"
                        disabled={submitting || !isDirty}
                        onClick={handleSave}
                    >
                        {submitting && <Spinner label={__('Saving')} className="mr-2" />}
                        {__('Save account changes')}
                    </button>
                </div>
            </div>
        </div>
    );
}
